import { useMemo, useState } from "react";
import { BellRing, Trash2 } from "lucide-react";
import {
  alertKey,
  isTriggered,
  readAlerts,
  writeAlerts,
  type PriceAlert,
} from "@/lib/price-alerts";
import { formatMoney, type CurrencyCode, type PriceRow } from "@/lib/prices-shared";

/** Small hook so the page and the detail panel share one alert list. */
export function usePriceAlerts() {
  const [alerts, setAlerts] = useState<PriceAlert[]>(() => readAlerts());
  const save = (next: PriceAlert[]) => {
    setAlerts(next);
    writeAlerts(next);
  };
  return {
    alerts,
    add: (a: PriceAlert) => save([a, ...alerts.filter((x) => alertKey(x) !== alertKey(a))]),
    remove: (key: string) => save(alerts.filter((x) => alertKey(x) !== key)),
    clear: () => save([]),
  };
}

/** Create-an-alert control shown inside the card detail panel. */
export function AlertCreator({
  row,
  currency,
  onAdd,
}: {
  row: PriceRow;
  currency: CurrencyCode;
  onAdd: (a: PriceAlert) => void;
}) {
  const [direction, setDirection] = useState<"above" | "below">("below");
  const [target, setTarget] = useState(() => (row.market * 0.85).toFixed(2));
  const [done, setDone] = useState(false);

  return (
    <div className="mt-4 rounded-xl border border-border/60 bg-card/40 p-3">
      <h3 className="flex items-center gap-1.5 text-sm font-bold">
        <BellRing className="h-4 w-4" /> Price alert
      </h3>
      <p className="mt-1 text-[11px] text-muted-foreground">
        Watch {row.code} and get a banner on this page the moment the market value crosses your target. Saved on this
        device, no account needed.
      </p>
      <form
        className="mt-2 flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const usd = Number(target);
          if (!Number.isFinite(usd) || usd <= 0) return;
          onAdd({
            code: row.code,
            name: row.name,
            direction,
            target: Math.round(usd * 100) / 100,
            createdAt: new Date().toISOString(),
            createdAtValue: row.market,
          });
          setDone(true);
        }}
      >
        <select
          aria-label="Alert direction"
          value={direction}
          onChange={(e) => setDirection(e.target.value as "above" | "below")}
          className="rounded-lg border border-border/60 bg-background/60 px-2 py-1.5 text-xs"
        >
          <option value="below">Drops to or below</option>
          <option value="above">Rises to or above</option>
        </select>
        <label className="flex items-center gap-1 text-xs">
          <span className="text-muted-foreground">USD</span>
          <input
            type="number"
            min="0.05"
            step="0.05"
            value={target}
            onChange={(e) => {
              setTarget(e.target.value);
              setDone(false);
            }}
            aria-label="Alert target in US dollars"
            className="w-24 rounded-lg border border-border/60 bg-background/60 px-2 py-1.5 text-xs tabular-nums"
          />
        </label>
        <button type="submit" className="rounded-lg border border-primary/60 px-3 py-1.5 text-xs font-semibold hover:bg-primary/10">
          {done ? "Alert saved" : "Set alert"}
        </button>
        <span className="text-[11px] text-muted-foreground">now {formatMoney(row.market, currency)}</span>
      </form>
    </div>
  );
}

/** Banner + manager for saved alerts, rendered above the price table. */
export function PriceAlertsPanel({
  alerts,
  rows,
  currency,
  onRemove,
  onClear,
  onOpen,
}: {
  alerts: PriceAlert[];
  rows: PriceRow[];
  currency: CurrencyCode;
  onRemove: (key: string) => void;
  onClear: () => void;
  onOpen: (code: string) => void;
}) {
  const byCode = useMemo(() => new Map(rows.map((r) => [r.code, r])), [rows]);
  if (!alerts.length) return null;
  const live = alerts.map((a) => ({ alert: a, row: byCode.get(a.code) }));
  const hit = live.filter((x) => x.row && isTriggered(x.alert, x.row.market));

  return (
    <section className="mt-6" aria-label="Your price alerts">
      {hit.length > 0 && (
        <div className="rounded-2xl border border-emerald-500/50 bg-emerald-500/10 p-4">
          <h2 className="flex items-center gap-1.5 text-sm font-bold text-emerald-300">
            <BellRing className="h-4 w-4" /> {hit.length} price alert{hit.length > 1 ? "s" : ""} triggered
          </h2>
          <ul className="mt-2 space-y-1 text-sm">
            {hit.map(({ alert, row }) => (
              <li key={alertKey(alert)}>
                <button onClick={() => onOpen(alert.code)} className="text-left hover:underline">
                  <strong>{alert.name}</strong> ({alert.code}) is {formatMoney(row!.market, currency)} —{" "}
                  {alert.direction === "below" ? "at or below" : "at or above"} your {formatMoney(alert.target, currency)} target.
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <details className="glass mt-3 rounded-2xl p-4">
        <summary className="cursor-pointer text-sm font-bold">Manage {alerts.length} saved price alert{alerts.length > 1 ? "s" : ""}</summary>
        <ul className="mt-3 space-y-1.5">
          {live.map(({ alert, row }) => (
            <li key={alertKey(alert)} className="flex items-center gap-2 text-xs">
              <span className="min-w-0 flex-1 truncate">
                <strong>{alert.name}</strong> {alert.code} · {alert.direction === "below" ? "≤" : "≥"}{" "}
                {formatMoney(alert.target, currency)}
                {row && <span className="text-muted-foreground"> · now {formatMoney(row.market, currency)}</span>}
              </span>
              <button onClick={() => onRemove(alertKey(alert))} aria-label={`Delete alert for ${alert.name}`} className="rounded p-1 hover:bg-card">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
        <button onClick={onClear} className="mt-3 rounded-lg border border-border/60 px-3 py-1.5 text-xs hover:border-primary/60">
          Clear all alerts
        </button>
      </details>
    </section>
  );
}
